import { User } from "@supabase/supabase-js";
import { cleanObject, EventName } from "core";
import { usePostHog } from "posthog-js/react";
import { useCallback } from "react";
import { useOptionalAuthContext } from "../auth/useAuthContext";
import { isDeployed } from "../config";
import { LocalStorageKeys } from "./cacheKeys";

function debugEvent(
  eventName: EventName,
  properties?: Record<string, unknown>,
) {
  // eslint-disable-next-line no-console
  console.log(
    `%c track %c ${eventName}`,
    "color: orange",
    "color: cyan; font-weight: bold",
    properties,
  );
}

enum UTMParam {
  Campaign = "utm_campaign",
  Content = "utm_content",
  Medium = "utm_medium",
  Source = "utm_source",
  Term = "utm_term",
}

enum AdClickParam {
  Gclid = "gclid",
  Gbraid = "gbraid",
  Wbraid = "wbraid",
  Fbclid = "fbclid",
  Msclkid = "msclkid",
}

type TrackingProperties = Partial<Record<UTMParam | AdClickParam, string>>;

function getTrackingParamsFromUrl(): TrackingProperties {
  const search = new URLSearchParams(window.location.search);
  const result: TrackingProperties = {};
  for (const param of [
    ...Object.values(UTMParam),
    ...Object.values(AdClickParam),
  ]) {
    const value = search.get(param);
    if (value != null) result[param] = value;
  }
  return result;
}

export function storeTrackingParamsIfPresent() {
  const params = getTrackingParamsFromUrl();
  if (Object.keys(params).length > 0) {
    localStorage.setItem(
      LocalStorageKeys.TrackingParams,
      JSON.stringify(params),
    );
  }
}

export function getStoredTrackingParams(): TrackingProperties {
  const stored = localStorage.getItem(LocalStorageKeys.TrackingParams);
  return stored ? (JSON.parse(stored) as TrackingProperties) : {};
}

export function clearStoredTrackingParams() {
  localStorage.removeItem(LocalStorageKeys.TrackingParams);
}

function getTrackingProperties(): TrackingProperties {
  const fromUrl = getTrackingParamsFromUrl();
  return Object.keys(fromUrl).length > 0 ? fromUrl : getStoredTrackingParams();
}

export function getUserProperties(user?: User) {
  return user
    ? {
        id: user.id,
        email: user.email,
      }
    : {};
}

/**
 * Capture user event into PostHog
 */
export function useTrackEvent() {
  const posthog = usePostHog();

  const { user } = useOptionalAuthContext();

  const track = useCallback(
    (eventName: EventName, properties?: Record<string, unknown>) => {
      const eventData = cleanObject({
        ...properties,
        ...getTrackingProperties(),
        ...getUserProperties(user),
      });

      if (!isDeployed) {
        debugEvent(eventName, eventData);
        return;
      }

      posthog.capture(eventName, eventData);
    },
    [posthog, user],
  );

  return {
    track,
  };
}
